import React, { useState, useEffect } from 'react';
import { useStripe, useElements, CardNumberElement } from '@stripe/react-stripe-js';

import AuthService from '../../services/api/auth';

import CardSection from './CardSection';
import LoadingModal from '../LoadingModal';
import ErrorList from '../ErrorList';
import Helper from '../../helpers';

const translate = trKey => window.VULOG_TRANSLATE(trKey);

const CardSetupForm = ({ history, pubKey }) => {
    const stripe = useStripe();
    const elements = useElements();

    const card = {
        number: false,
        expiryDate: false,
        cvc: false,
    };

    const [isCardReady, setIsCardReady] = useState(card);
    const [cardHolderName, setCardHolderName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [postalCode, setPostalCode] = useState('');

    useEffect(() => {
        const urlParams = Helper.getUrlParams();
        if (urlParams && urlParams.error) {
            setErrors([urlParams.error]);
        }
    }, []);

    const onCardChange = (event, type) => {
        switch (type) {
            case 'number':
            case 'expiryDate':
            case 'cvc':
                if (event.complete) {
                    setIsCardReady({ ...isCardReady, [type]: true });
                } else if (event.error) {
                    setIsCardReady({ ...isCardReady, [type]: false });
                }
                break;

            default:
                break;
        }
    };

    const isCardCompleted = () => {
        // all card values are true
        const areAllCardValuesTrue =
            (isCardReady && Object.values(isCardReady) && Object.values(isCardReady).every(val => val)) || false;
        const isPostalCodeMandatory = !window.VULOG_CONFIG.STRIPE_HIDE_POSTAL_CODE;
        const minPostalCodeLength = window.VULOG_CONFIG.STRIPE_POSTAL_CODE_MIN_LENGTH || 5;
        const maxPostalCodeLength = window.VULOG_CONFIG.STRIPE_POSTAL_CODE_MAX_LENGTH || 5;
        const isNum = val => /^\d+$/.test(val);
        if (isPostalCodeMandatory) {
            return (
                areAllCardValuesTrue &&
                isNum(postalCode) &&
                postalCode.length >= minPostalCodeLength &&
                postalCode.length <= maxPostalCodeLength &&
                cardHolderName
            );
        }
        return areAllCardValuesTrue && cardHolderName;
    };

    const handleSubmit = async event => {
        // We don't want to let default form submission happen here,
        // which would refresh the page.
        event.preventDefault();

        setIsLoading(true);

        try {
            const { mopData } = await AuthService.postSetupIntent({ paymentMethodType: 'CREDIT_CARD' });
            const { pspClientSecret } = mopData;
            const blockPrepaidCard = window.VULOG_CONFIG.BLOCK_PREPAID_CARD;

            if (!stripe || !elements) {
                // Stripe.js has not yet loaded.
                // Make sure to disable form submission until Stripe.js has loaded.
                return;
            }

            const data = {
                card: elements.getElement(CardNumberElement),
                billing_details: {
                    name: cardHolderName,
                    address: {
                        postal_code: postalCode,
                    },
                },
            };

            const result = await stripe.createPaymentMethod({
                type: 'card',
                ...data,
            });

            if (blockPrepaidCard && result.paymentMethod.card.funding === 'prepaid') {
                setErrors(['PAYMENT_METHOD_FAILED']);
                setIsLoading(false);
                return;
            }

            const response = await stripe.confirmCardSetup(
                pspClientSecret,
                {
                    payment_method: {
                        ...data,
                    },
                    return_url: `${window.location.origin}/register/stripe_redirection?pubKey=${pubKey}`,
                },
                { handleActions: false }
            );

            if (response.error) {
                setErrors([response.error.message]);
                setIsLoading(false);
            } else if (response.setupIntent.status === 'requires_action') {
                window.location = response.setupIntent.next_action.redirect_to_url.url;
            } else {
                // The payment has been processed!
                if (response.setupIntent.status === 'succeeded') {
                    AuthService.stripeValidatePayment().then(res => {
                        res.next ? history.push(res.next) : history.push('/register/status');
                    });
                }
            }
        } catch (err) {
            console.log('err', err);
            if (err.message) {
                setErrors([err.message]);
            }
            setIsLoading(false);
        }
    };
    return (
        <>
            <form onSubmit={handleSubmit} className={'stripeForm'}>
                {window.VULOG_CONFIG.ERROR_DISPLAY_TOP && <ErrorList errors={errors} />}

                <CardSection onCardChange={onCardChange} setPostalCode={setPostalCode} />

                <label htmlFor={'cardHolderName'} className={'blue small mt-3 mb-0 isRequired'}>
                    {translate('Card holder name')}
                </label>
                <input
                    type="text"
                    name="cardHolderName"
                    className="form-control"
                    id={'cardHolderName'}
                    onChange={e => setCardHolderName(e.target.value)}
                />

                <div className="alert alert-info mt-4 font-weight-light" role="alert">
                    {translate('authoriseToTakePaymentFromMyCardAccount')}
                </div>

                {!window.VULOG_CONFIG.ERROR_DISPLAY_TOP && <ErrorList errors={errors} />}

                <div className="flex-row mt-4 mb-2rem">
                    <button className="btn btn-primary btn-block" disabled={!stripe || !isCardCompleted()}>
                        {translate('Save Card')}
                    </button>
                </div>
            </form>

            <LoadingModal loading={isLoading} />
        </>
    );
};

export default CardSetupForm;
